import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'

interface CounterProps {
  value: number
  className?: string
  suffix?: string
  prefix?: string
}

export function Counter({ value, className, suffix = '', prefix = '' }: CounterProps) {
  const [displayValue, setDisplayValue] = useState(value)
  const [isAnimating, setIsAnimating] = useState(false)

  useEffect(() => {
    if (value !== displayValue) {
      setIsAnimating(true)
      // Animate from current value to new value
      const steps = Math.min(Math.abs(value - displayValue), 20)
      const increment = (value - displayValue) / steps
      let current = displayValue
      let step = 0

      const interval = setInterval(() => {
        step++
        current += increment
        if (step >= steps) {
          setDisplayValue(value)
          setIsAnimating(false)
          clearInterval(interval)
        } else {
          setDisplayValue(Math.round(current))
        }
      }, 50)

      return () => clearInterval(interval)
    }
  }, [value, displayValue])

  const digits = displayValue.toString().split('')

  return (
    <div className={cn('font-mono inline-flex items-center', className)}>
      {prefix && <span className="mr-1">{prefix}</span>}
      <AnimatePresence mode="popLayout">
        {digits.map((digit, index) => (
          <motion.span
            key={`${index}-${digit}`}
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={cn(
              'inline-block tabular-nums',
              isAnimating && 'text-gold-400'
            )}
          >
            {digit}
          </motion.span>
        ))}
      </AnimatePresence>
      {suffix && <span className="ml-1">{suffix}</span>}
    </div>
  )
}

interface CountdownDisplayProps {
  days: number
  hours: number
  minutes: number
  seconds: number
  className?: string
}

export function CountdownDisplay({ days, hours, minutes, seconds, className }: CountdownDisplayProps) {
  const units = [
    { value: days, label: 'days' },
    { value: hours, label: 'hrs' },
    { value: minutes, label: 'min' },
    { value: seconds, label: 'sec' },
  ]

  return (
    <div className={cn('flex gap-3', className)}>
      {units.map((unit, index) => (
        <div key={unit.label} className="flex items-center">
          <div className="flex flex-col items-center">
            <div className="bg-midnight-800/80 border border-slate-700/50 rounded-lg px-3 py-2 min-w-[3rem]">
              <motion.span
                key={unit.value}
                initial={{ y: -10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="block text-xl font-mono font-bold text-gold-400 text-center"
              >
                {unit.value.toString().padStart(2, '0')}
              </motion.span>
            </div>
            <span className="text-xs text-slate-500 mt-1 uppercase tracking-wider">
              {unit.label}
            </span>
          </div>
          {index < units.length - 1 && (
            <span className="text-slate-600 mx-1 text-xl self-start mt-2">:</span>
          )}
        </div>
      ))}
    </div>
  )
}
